export type WorkflowStatus =
  | 'RASCUNHO'
  | 'PENDENTE'
  | 'EM_PREENCHIMENTO'
  | 'AGUARDANDO_APROVACAO'
  | 'EM_ANALISE'
  | 'REJEITADA'
  | 'APROVADA'
  | 'BLOQUEADA'
  | 'TRANSBORDADA'
  | 'CONCILIADO_AUTOMATICAMENTE'
  | 'DIVERGENCIA_DETECTADA'
  | 'EM_CONFERENCIA'
  | 'OBSOLETA';

export interface PdcaPilares { pessoas: string; processos: string; tecnologia: string; }
export interface PdcaConteudo { plan: PdcaPilares; do: PdcaPilares; check: PdcaPilares; act: PdcaPilares; }

export interface JustificativaTemplate {
  templateId: number;
  titulo: string;
  justificativaDetalhada: string;
  pdca: PdcaConteudo;
}

export type PlanoMelhoriaStatus = 'NAO_INICIADO' | 'EM_ANDAMENTO' | 'AGUARDANDO_VALIDACAO' | 'CONCLUIDO' | 'VALIDADO';
export type PdcaFase = 'plan' | 'do' | 'check' | 'act';
export type PdcaPilar = 'PESSOAS' | 'PROCESSOS' | 'TECNOLOGIA';

export interface PlanoMelhoriaAcao {
  acaoId: number;
  planoId: number;
  responsavelId: number | null;
  responsavelNome: string | null;
  descricao: string;
  prazo: string | null;
  status: PlanoMelhoriaStatus;
  dataConclusao: string | null;
  evidencia: string | null;
  observacao: string | null;
  pilar: PdcaPilar;
  fase: 'PLAN' | 'DO' | 'CHECK' | 'ACT';
}

export interface PlanoMelhoriaAcaoInput {
  responsavelId?: number | null;
  descricao: string;
  prazo?: string | null;
  status: PlanoMelhoriaStatus;
  dataConclusao?: string | null;
  evidencia?: string | null;
  observacao?: string | null;
  pilar: PdcaPilar;
  fase: 'PLAN' | 'DO' | 'CHECK' | 'ACT';
}

export interface PlanoMelhoria {
  planoId: number;
  concilId: number;
  templateId: number | null;
  tituloTemplate: string | null;
  justificativaDetalhada: string;
  pdca: PdcaConteudo;
  status: PlanoMelhoriaStatus;
  checkValidado: boolean;
  checkValidadoPorNome: string | null;
  checkValidadoEm: string | null;
  checkObservacao: string | null;
  atoRegistrado: boolean;
  atoTexto: string | null;
  atoRegistradoPorNome: string | null;
  atoRegistradoEm: string | null;
  podeEncerrar: boolean;
  acoes: PlanoMelhoriaAcao[];
}

export type MotivoDevolucao =
  | 'DOCUMENTACAO_INCOMPLETA'
  | 'DIVERGENCIA_NAO_JUSTIFICADA'
  | 'NECESSIDADE_EVIDENCIA_COMPLEMENTAR';

export const MOTIVOS_DEVOLUCAO: { value: MotivoDevolucao; label: string }[] = [
  { value: 'DOCUMENTACAO_INCOMPLETA',           label: 'Documentação Incompleta' },
  { value: 'DIVERGENCIA_NAO_JUSTIFICADA',       label: 'Divergência Não Justificada' },
  { value: 'NECESSIDADE_EVIDENCIA_COMPLEMENTAR', label: 'Necessidade de Evidência Complementar' },
];

export type StatusDivergencia = 'CONCILIADO' | 'DIVERGENCIA_DETECTADA' | 'JUSTIFICADO';

export type StepStatus =
  | 'NAO_INICIADA'
  | 'EM_ANDAMENTO'
  | 'CONCLUIDA'
  | 'COM_ERRO'
  | 'REJEITADA';

export type RiskLevel = 'BAIXO' | 'MEDIO' | 'ALTO';

export interface WorkflowStep {
  id: number;
  stepId: string;
  nome: string;
  status: StepStatus;
  responsavel?: string;
  cargo?: string;
  concluidoEm?: string;
  autenticador?: string;
  erros: number;
  pendencias: number;
  comentario?: string;
}

export interface WorkflowConciliacao {
  id: number;
  // Id do cartão (conta) no Motor de Conciliação — permite abrir a Capa de Conciliação real
  // (/capa-conciliacao/:idCartao), a mesma tela usada pelo Mapa de Conciliação. Nulo para
  // conciliações submetidas antes desse vínculo existir.
  cartaoId: number | null;
  empresaId: number;
  empresa: string;
  cnpj: string;
  conta: string;
  descricaoConta: string;
  periodo: string;
  valorTotal: number;
  saldoContabil: number;
  saldoConciliado: number;
  saldoSuporte: number;
  divergencia: number;
  etapaAtual: number;
  nomeEtapa: string;
  nivelAtual: number;
  // Nível hierárquico N1-N7 de quem está com a pendência agora (cargo do grupo ou, se
  // devolvida para um indivíduo específico, o cargo dele) — não confundir com nivelAtual,
  // que é o passo 1-6 do processo (Cadastro..Encerramento).
  responsavelNivel: number | null;
  responsavelAtual: string;
  responsavelCargo: string | null;
  autor: string;
  /** wf_usuarios.user_id de quem elaborou (SoD). */
  autorId?: number | null;
  status: WorkflowStatus;
  risco: RiskLevel;
  sla: string;
  criadoEm?: string | null;
  atualizadoEm?: string | null;
  hashIntegridade: string;
  steps: WorkflowStep[];
  evidencias: { evidenciaId: number; nomeArquivo: string; tipoMime: string | null; tamanhoBytes: number | null; enviadoEm: string }[];
  // Integração Motor de Conciliação
  justificativaTecnica: string | null;
  justificativaDetalhada: string | null;
  planoAcaoCorretiva: string | null;
  statusDivergencia: StatusDivergencia;
  justificativaObrigatoria: boolean;
  podeEnviarAprovacao: boolean;
  // Devolução Hierárquica
  motivoDevolucao?: string | null;
  devolvidoParaUserId?: number | null;
  devolvidoParaNome?: string | null;
}

export interface PendenciaTransversal {
  concilId: number;
  empresa: string;
  conta: string;
  descricaoConta: string;
  cicloOriginal: string;
  cicloAtual: string;
  problema: string;
  prazo: string | null;
  agingDias: number;
  status: 'EM_TRATAMENTO' | 'VENCIDA';
  herdada: boolean;
}

export interface PendenciasTransversaisResponse {
  dividaTecnicaAtual: number;
  abertas: number;
  herdadas: number;
  vencidas: number;
  agingMedioDias: number;
  itens: PendenciaTransversal[];
}

export interface ManualEntry {
  id: number;
  data: string;
  empresa: string;
  empresaId: number;
  conta: string;
  cartaoId: number | null;
  historico: string;
  valor: number;
  autor: string;
  risco: RiskLevel;
  status: WorkflowStatus;
  justificativa: string;
  planoAcao: string;
  agingDias: number;
}

export interface SoxEvent {
  id: number;
  dataHora: string;
  executor: string;
  titular: string;
  nivel: number;
  acao: string;
  etapa: string;
  ip: string;
  userAgent: string;
  hashAnterior: string;
  hashAtual: string;
  justificativa: string;
  /** O que foi afetado (ex.: usuário e empresa de uma revogação de acesso). */
  escopo: string;
  modulo: string;
}

export type FechamentoEventoTipo = 'ABERTURA' | 'PRAZO_NIVEL' | 'AUDITORIA' | 'ENCERRAMENTO' | 'FERIADO';
export type FechamentoEventoStatus = 'CONCLUIDO' | 'EM_ANDAMENTO' | 'PENDENTE' | 'ATRASADO';

export interface FechamentoEvento {
  id: number;
  data: string; // formato AAAA-MM-DD
  titulo: string;
  tipo: FechamentoEventoTipo;
  nivel?: number;
  status: FechamentoEventoStatus;
  empresa: string;
  descricao: string;
}

export interface LancamentoConta {
  id: number;
  conciliacaoId: number;
  data: string;
  historico: string;
  valor: number;
  tipo: 'CREDITO' | 'DEBITO';
  natureza: 'AJUSTE' | 'COMPOSICAO';
  codigoContabil: string;
}

export interface FaixaAging {
  faixa: string;
  valor: number;
  percentual: number;
}

export interface GrupoEconomico {
  grupo_id: number;
  nome: string;
  tenant_ids: number[];
  responsavel: string;
  responsavelId?: number | null;
  status: 'ATIVO' | 'INATIVO';
}

export interface UsuarioWorkflow {
  id: number;          // wf_usuarios.user_id (PK interno)
  authUserId?: number; // wf_usuarios.auth_user_id (FK para usuario.id)
  nome: string;
  email: string;
  cargo: string;
  nivel: number;
  ativo: boolean;
  adminTi: boolean;
  bloqueado?: boolean; // bloqueado no cadastro de usuários: não entra em seleções
  empresaIds: number[];
}

export interface EmpresaTenant {
  id: number;
  empresaId?: number;
  nome: string;
  cnpj: string;
  grupoId: number | null;
  responsavelId: number | null;
  status: 'ATIVA' | 'INATIVA' | 'ORFA' | 'BLOQUEADA';
}

export interface UsuarioSistema {
  id: number;
  nome: string;
  email: string;
  nomeUsuario: string;
  perfil: string;
  ativo: boolean;
  bloqueado?: boolean;
  // nivel e cargo vindos do wf_usuarios (null se não atribuído)
  wfUserId?: number;
  wfNivel?: number;
  wfCargo?: string;
  wfAtivo?: boolean;
  wfAdminTi?: boolean;
}

export interface MatrizPermissao {
  id: number;
  usuarioId: number;
  usuarioNome: string;
  empresaId: number;
  empresaNome: string;
  concedidoPor: string;
  dataConcessao: string;
  justificativa: string;
}

export type ScopeType = 'GROUP' | 'SUBGROUP' | 'SINGLE';
export type AlertaStatus = 'OK' | 'ALERTA' | 'ERRO';

export interface ConfigAlcadaNivel {
  id: number;
  etapaId: number;
  cargoId: number;
  nivel: number;
  cargo: string;
  scopeType: ScopeType;
  scopeLabel: string;
  scopeTenantIds: number[];
  valorMin: number;
  valorMax: number | null;
  slaDiasUteis: number;
  titularId: number | null;
  titular: string;
  substitutoId: number | null;
  substituto: string;
  status: AlertaStatus;
  alertas: string[];
}

export type SLAStatus = 'VERDE' | 'AMARELO' | 'VERMELHO';

export interface MonitoramentoItem {
  id: number;
  conciliacaoId: number;
  empresa: string;
  grupoNome: string;
  scopeType: ScopeType;
  valor: number;
  etapa: string;
  nivel: number;
  responsavel: string;
  responsavelEmail?: string;
  dataLimite: string;
  /** Prazo no formato ISO do backend (dataLimite é só o texto dd/MM/aaaa para exibição). */
  dataLimiteIso: string | null;
  slaStatus: SLAStatus;
  horasRestantes: number;
  alertaEnviado: boolean;
  escalado: boolean;
}

export interface HistoricoSOXItem {
  id: number;
  acao: string;
  usuario: string;
  timestamp: string;
  justificativa: string;
  hashSnapshot: string;
  escopo: string;
  modulo: string;
}

export interface EvidenciaAnexo {
  id: number;
  nome: string;
  tamanho: string;
  tipo: string;
  dataUpload: string;
  usuario: string;
}

// ─── Matriz SOX ──────────────────────────────────────────────────────────────

export interface MatrizArvoreNo {
  id: string;              // "g-{grupoId}" | "t-{tenantId}"
  nome: string;
  tipo: 'GRUPO' | 'EMPRESA';
  paiId: string | null;
  tenantId: number | null;
  empresaId: number | null;   // empresa.id — chave do De/Para
  cnpj: string | null;
  filhosCount: number;
}

export interface MatrizContaContabil {
  contaId: number;
  tenantId: number;
  codigo: string;
  codigoReduzido: string;
  descricao: string;
  tipo: 'ANALITICA' | 'SINTETICA';
  nivel: number;
  grupoConta: string | null;
  contaPaiCodigo: string | null;
  bloqueada: boolean;
  aprovadorUserId: number | null;
  aprovadorNome: string | null;
  cargoId: number | null;
  nomeCargo: string | null;
  statusVinculo: 'PENDENTE' | 'VINCULADO' | 'BLOQUEADO';
  semReferencial?: boolean;
}

export interface MatrizUsuarioApto {
  userId: number;
  nome: string;
  email: string;
  cargo: string;
  nivelHierarquico: number;
  aptoSod: boolean;
  motivoInaptidao: string | null;
}

export interface MatrizLogEntry {
  logId: number;
  tenantId: number;
  contaId: number;
  contaCodigo: string;
  aprovadorAnteriorNome: string | null;
  aprovadorNovoNome: string;
  usuarioLogadoNome: string;
  acaoEm: string;
  hashValidacao: string;
  acao: string;
  justificativaSox: string | null;
}
